<template>
  <div class="AtlasControls">
    <atlas-widget-dropdown
      v-if="breakdownLinks.length > 1"
      id="tour-selectBreakdown"
      class="AtlasControls-filter"
      :options="breakdownLinks"
      @choose="setActiveBreakdown"
      >Select Breakdown</atlas-widget-dropdown
    >

    <atlas-widget-dropdown
      class="AtlasControls-filter"
      v-if="showGeoCompare"
      :options="geoCompareLinks"
      >Compare</atlas-widget-dropdown
    >

    <atlas-widget-dropdown
      class="AtlasControls-filter"
      v-if="geoTypeLinks.length"
      :options="geoTypeLinks"
      @choose="handleMapGeographySelection"
    >
      Map Geography
    </atlas-widget-dropdown>

    <span class="AtlasControls-label" v-if="filterLinks.length" id="tour-filter"
      >Filters:</span
    >

    <atlas-widget-dropdown
      class="AtlasControls-filter"
      v-for="filter in filterLinks"
      :key="filter.label"
      :options="filter.options"
      @choose="handleFilterSelection"
    >
      {{ filter.label }}
    </atlas-widget-dropdown>

    <atlas-widget-dropdown
      v-if="breakdown.slider"
      class="AtlasControls-filter"
      detached
      keep-open
    >
      {{ breakdown.slider.label }}
      <template v-slot:content>
        <atlas-map-filters
          v-bind="breakdown.slider"
          :breakdown-id="breakdownId"
        />
      </template>
    </atlas-widget-dropdown>

    <atlas-widget-dropdown class="AtlasControls-filter" v-if="allowDownload">
      <span id="tour-download" title="Download">
        <atlas-icon name="download" :size="22" />
      </span>
      <template v-slot:content>
        <atlas-download
          :breakdown-id="breakdownId"
          :disabledDownloadImage="disabledDownloadImage"
          :disabledDownloadPowerpoint="disabledDownloadPowerpoint"
          :disabledDownloadXlsx="disabledDownloadXlsx"
        />
      </template>
    </atlas-widget-dropdown>

    <atlas-widget-dropdown class="AtlasControls-filter" v-if="allowShare">
      <span id="tour-share" title="Share">
        <atlas-icon name="share" :size="22" />
      </span>
      <template v-slot:content>
        <atlas-share />
      </template>
    </atlas-widget-dropdown>

    <!--span
    class="AtlasControls-icon"
    v-if="allowTour"
    title="Take the Tour"
    @click.prevent="launchTour"
    ><atlas-icon name="help-circle" :size="18" /></span-->

    <span
      class="AtlasControls-icon"
      v-if="allowFullscreen"
      :title="fullscreenTitle"
      @click.prevent="toggleFullscreen"
    >
      <atlas-icon
        :name="fullscreenIcon"
        :size="26"
        style="position: absolute; margin-top: 2px"
      />
    </span>

    <div class="AtlasControls-spinner" v-if="isLoading">
      <atlas-spinner />
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapMutations } from 'vuex';
import AtlasWidgetDropdown from './dropdown.vue';
import AtlasShare from './share.vue';
import AtlasMapFilters from './map-filters.vue';
import AtlasDownload from './download.vue';

const BREAKDOWN_ICONS = {
  line: 'lines',
  bar: 'bars--horizontal',
  row: 'bars--horizontal',
  column: 'bars--vertical',
  rank: 'bars--vertical',
  map: 'map',
};

export default {
  components: {
    AtlasDownload,
    AtlasWidgetDropdown,
    AtlasShare,
    AtlasMapFilters,
  },

  props: {
    geoType: {
      type: String,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },
    breakdownId: {
      type: String,
    },
    disabledDownloadImage: { type: Boolean },
    disabledDownloadPowerpoint: { type: Boolean },
    disabledDownloadXlsx: { type: Boolean },
  },

  computed: {
    ...mapState('ui', ['isFullscreen']),

    ...mapState('breakdown', ['secondaryGeo', 'geos']),

    ...mapGetters('display', [
      'displayBreakdownSelect',
      'allowShare',
      'allowDownload',
      'allowFullscreen',
    ]),
    ...mapState('breakdown', {
      activeGeoType: 'geoType',
    }),

    ...mapGetters('indicator', ['breakdowns', 'defaultBreakdown']),

    ...mapGetters('breakdown', ['breakdownById', 'filtersByBreakdownId']),

    breakdown() {
      return this.breakdownById(this.breakdownId);
    },
    filters() {
      return this.filtersByBreakdownId(this.breakdownId);
    },

    query() {
      return (this.$route || {}).query || {};
    },

    queryWithoutFilters() {
      return Object.fromEntries(
        Object.entries(this.query).filter((query) => {
          return !this.filters.some((filter) => filter.name === query[0]);
        }),
      );
    },

    breakdownLinks() {
      return ((this.displayBreakdownSelect && this.breakdowns) || []).map(
        (breakdown) => {
          const query = {
            ...this.queryWithoutFilters,
            breakdown: breakdown.slug,
          };
          // geo compare option should not be persistent
          delete query.geo_compare;

          return {
            to: { query },
            isActive: this.getActiveBreakdown(breakdown.slug),
            isDisabled: breakdown.config.isDisabled,
            text: breakdown.config.name,
            icon: BREAKDOWN_ICONS[breakdown.config.subtype] || 'lines',
          };
        },
      );
    },

    geoCompareOptions() {
      if (!this.breakdown.geoCompareOptions) return undefined;

      const keys = this.breakdown.geoCompareOptions.reduce(
        (acc, geo) => ({ ...acc, [geo]: true }),
        {},
      );

      return this.geos.filter((geo) => keys[geo.geo_code]);
    },

    geoCompareLinks() {
      return (
        this.geoCompareOptions && [
          {
            isActive: !this.query.geo_compare,
            text: 'No comparison',
          },

          ...this.geoCompareOptions.map((geo) => ({
            isActive: this.query.geo_compare === geo.geo_code,
            text: geo.geo_display_name || geo.geo_name,
          })),
        ]
      );
    },

    showGeoCompare() {
      return Boolean(
        this.breakdown.allowGeoCompare &&
          this.geoCompareLinks &&
          this.geoCompareLinks.length,
      );
    },

    queryString() {
      return JSON.stringify(this.query);
    },

    filterLinks() {
      return this.filters.map(({ label, options, defaultValue }) => ({
        label,
        options: options
          .filter((x) => x)
          .map((filter) => {
            const [key, value] = filter.name.split(':');
            const query = { ...this.query };

            if (value === defaultValue) {
              delete query[key];
            } else {
              query[key] = value;
            }

            return {
              to: { query },
              text: filter.label,
              isActive: JSON.stringify(query) === this.queryString,
            };
          }),
      }));
    },

    geoTypeLinks() {
      return (this.breakdown.geoTypeFilters || []).map(({ text, value }, i) => {
        const query = { ...this.query };

        if (!i) delete query.geo_type;
        else query.geo_type = value;

        return {
          to: { query },
          text,
          isActive: JSON.stringify(query) === this.queryString,
          value,
        };
      });
    },

    fullscreenTitle() {
      return this.isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen';
    },

    fullscreenIcon() {
      return `fullscreen--${this.isFullscreen ? 'exit' : 'enter'}`;
    },
  },

  watch: {
    $route() {
      // Always destroy the component on any route change
      this.$store.commit('ui/closeDropdown');
    },
    geoTypeLinks() {
      const activeGeoTypeLink = this.geoTypeLinks.find(
        (geoType) => geoType.isActive,
      );

      // TODO: review needs
      // It's unclear what `geoTypesDefaultPositions` is.
      if (
        this.breakdown?.geoTypesDefaultPositions &&
        activeGeoTypeLink &&
        activeGeoTypeLink?.value !== this.activeGeoType
      ) {
        const activeGetTypeWithPos = this.breakdown?.geoTypesDefaultPositions.find(
          (geoType) => {
            return activeGeoTypeLink.value === geoType.value;
          },
        );
        if (activeGetTypeWithPos) {
          this.setActiveGeoType(activeGetTypeWithPos);
        }
      }
    },
  },

  methods: {
    getActiveBreakdown(slug) {
      if (this.query?.breakdown) {
        return this.query.breakdown === slug;
      }
      if (this.breakdownId) {
        return this.breakdownId === slug;
      }
      return this.defaultBreakdown.slug === slug;
    },

    async syncControlsInQuery(option) {
      const { query } = option;

      await this.$router.push({ ...this.$route, query });
    },

    async setActiveBreakdown({ option }) {
      // order matters! args can affect breakdown context
      // so syncing query params first
      // then triggering breakdown change
      await this.syncControlsInQuery(option);

      this.$emit('setActiveBreakdown', option.query.breakdown);
    },

    ...mapMutations('ui', ['toggleFullscreen']),
    ...mapMutations('breakdown', ['setActiveGeoType']),

    handleMapGeographySelection({ option }) {
      this.syncControlsInQuery(option);
    },
    handleFilterSelection({ option }) {
      this.syncControlsInQuery(option);
    },
  },
};
</script>

<style lang="scss">
.AtlasControls {
  display: flex;
  flex-wrap: wrap;
  background-color: color(white);

  &-label {
    display: inline-block;
    padding: 13px $gutter 10px;
    color: color(label--darken);
    font-weight: bold;
    line-height: 22px;
    text-transform: uppercase;
    vertical-align: top;
  }

  &-spinner {
    @include size(45px);
    display: inline-block;
    padding: 13px $gutter 10px;
    vertical-align: top;
  }

  &-icon {
    background-color: color(bg);
    position: relative;
    display: inline-block;
    padding: 13px 15px 10px;
    color: color(primary);
    cursor: pointer;
    line-height: 22px;
    vertical-align: top;
    width: 48px;
    height: 45px;

    &:hover {
      color: color(black);
    }
  }
}
</style>
